import { useState, useEffect, useRef } from 'react';
import { Send, ArrowLeft } from 'lucide-react';
import { collection, doc, setDoc, addDoc, query, orderBy, onSnapshot, serverTimestamp, increment } from 'firebase/firestore';
import { db } from '../firebase';
import { warmUserNames, getResolvedUsers, type ResolvedUser } from '../api/users';
import type { User } from 'firebase/auth';

const ADMIN_UID = 'bZj8y9DApEac3eKOuKV08MKcaDx2';

function formatMsgTime(seconds: number): string {
  if (!seconds) return '';
  const d = new Date(seconds * 1000);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (isToday) return time;
  if (isYesterday) return `Yesterday ${time}`;
  return `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${time}`;
}

interface ChatThread {
  uid: string;
  name: string;
  email: string;
  lastMessage: string;
  lastAt: number;
  unreadAdmin: number;
  plan: 'pro' | 'trial' | 'code' | 'expired';
}

interface Message {
  id: string;
  text: string;
  from: 'user' | 'admin';
  createdAt: number;
}

export default function AdminChat({ user }: { user: User }) {
  const isAdmin = user.uid === ADMIN_UID;
  const [threads, setThreads] = useState<ChatThread[]>([]);
  const [activeUid, setActiveUid] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);

  // Load all chat threads with unread counts, and listen to each user's
  // subscription state in real-time so plan chips stay current.
  const userUnsubs = useRef<(() => void)[]>([]);
  const planCache = useRef<Record<string, 'pro' | 'trial' | 'code' | 'expired'>>({});
  const nameCache = useRef<Record<string, { name: string; email: string }>>({});
  // Names/emails resolved live from Firebase Auth (via callable) for accounts
  // that have no name in Firestore — e.g. gift-code redeemers. Resolved once
  // per uid per session.
  const [resolved, setResolved] = useState<Record<string, ResolvedUser>>(() => getResolvedUsers());

  useEffect(() => {
    if (!isAdmin) return;
    return onSnapshot(collection(db, 'chats'), (snap) => {
      // Clean up previous user-doc listeners
      userUnsubs.current.forEach((u) => u());
      userUnsubs.current = [];

      const uids = snap.docs.filter((d) => d.id !== ADMIN_UID).map((d) => d.id);
      const chatData = Object.fromEntries(
        snap.docs.filter((d) => d.id !== ADMIN_UID).map((d) => [d.id, d.data()])
      );

      // Resolve real names/emails for any uids not already known. The cache is
      // usually pre-warmed at app load (AppShell's chats listener), so this is a
      // no-op refresh; setResolved syncs in any newly-resolved names.
      warmUserNames(uids).then(setResolved);

      // Listen to each user's doc for real-time plan updates
      uids.forEach((uid) => {
        const unsub = onSnapshot(doc(db, 'users', uid), (userSnap) => {
          let plan: 'pro' | 'trial' | 'code' | 'expired' = 'expired';
          if (userSnap.exists()) {
            const data = userSnap.data();
            if (data.paidVia === 'code') plan = 'code';
            else if (data.subscriptionStatus === 'trialing') plan = 'trial';
            else if (data.paid === true) plan = 'pro';
            if (data.displayName || data.email) {
              nameCache.current[uid] = { name: data.displayName || '', email: data.email || '' };
            }
          }
          planCache.current[uid] = plan;

          // Rebuild threads with latest plans
          const list: ChatThread[] = uids.map((id) => ({
            uid: id,
            name: chatData[id]?.userName || nameCache.current[id]?.name || id,
            email: chatData[id]?.userEmail || nameCache.current[id]?.email || '',
            lastMessage: '',
            lastAt: chatData[id]?.updatedAt?.seconds || 0,
            unreadAdmin: chatData[id]?.unreadAdmin || 0,
            plan: planCache.current[id] || 'expired',
          }));
          list.sort((a, b) => b.lastAt - a.lastAt);
          setThreads(list);
        });
        userUnsubs.current.push(unsub);
      });
    });
  }, []);

  // Load messages for active thread
  useEffect(() => {
    if (!activeUid) return;
    // Reset unread count when opening
    setDoc(doc(db, 'chats', activeUid), { unreadAdmin: 0 }, { merge: true }).catch(() => {});
    const q = query(collection(db, 'chats', activeUid, 'messages'), orderBy('createdAt', 'asc'));
    return onSnapshot(q, (snap) => {
      setMessages(snap.docs.map((d) => ({
        id: d.id,
        text: d.data().text,
        from: d.data().from,
        createdAt: d.data().createdAt?.seconds || 0,
      })));
    });
  }, [activeUid]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || !activeUid) return;
    const text = input.trim();
    setInput('');
    await addDoc(collection(db, 'chats', activeUid, 'messages'), {
      text,
      from: 'admin',
      createdAt: serverTimestamp(),
    });
    // Increment unread count for user + update timestamp
    await setDoc(doc(db, 'chats', activeUid), {
      updatedAt: serverTimestamp(),
      unreadUser: increment(1),
    }, { merge: true });
  };

  if (!isAdmin) return null;

  return (
    <div className="admin-chat">
      {!activeUid ? (
        <>
          <div className="admin-chat-threads">
            {threads.length === 0 && <p className="admin-chat-empty">No conversations yet</p>}
            {threads.map((t) => (
              <button key={t.uid} className="admin-chat-thread" onClick={() => setActiveUid(t.uid)}>
                <div className="admin-chat-thread-top">
                  <span className="admin-chat-thread-name">{resolved[t.uid]?.name || t.name}</span>
                  {t.unreadAdmin > 0 && <span className="admin-chat-thread-badge">{t.unreadAdmin}</span>}
                  <span className={`plan-chip plan-chip--${t.plan}`}>{{ pro: 'PRO', trial: 'TRIAL', code: 'GIFT', expired: 'EXPIRED' }[t.plan]}</span>
                </div>
                <span className="admin-chat-thread-email">{t.email || resolved[t.uid]?.email}</span>
              </button>
            ))}
          </div>
        </>
      ) : (
        <>
          <div className="admin-chat-header">
            <button className="admin-chat-back" onClick={() => setActiveUid(null)}>
              <ArrowLeft size={18} />
            </button>
            <span>{resolved[activeUid]?.name || threads.find((t) => t.uid === activeUid)?.name || activeUid}</span>
            {(() => { const t = threads.find((t) => t.uid === activeUid); return t ? <span className={`plan-chip plan-chip--${t.plan}`}>{{ pro: 'PRO', trial: 'TRIAL', code: 'GIFT', expired: 'EXPIRED' }[t.plan]}</span> : null; })()}
          </div>
          <div className="admin-chat-messages">
            {messages.map((m) => (
              <div key={m.id} className={`chat-msg chat-msg--${m.from}`}>
                <span>{m.text}</span>
                <span className="chat-msg-time">{formatMsgTime(m.createdAt)}</span>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
          <form className="chat-input-bar" onSubmit={(e) => { e.preventDefault(); handleSend(); }}>
            <input
              className="chat-input"
              placeholder="Reply..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
            />
            <button className="chat-send" type="submit" disabled={!input.trim()}>
              <Send size={16} />
            </button>
          </form>
        </>
      )}
    </div>
  );
}
